import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../../../lib/api";
import { circlesQueryKey } from "./useCircleData";

interface UseCircleMutationsOptions {
  readonly activeCircleId: string | null;
  readonly onCloseCreate: () => void;
  readonly onCloseInvite: () => void;
  readonly onCloseRename: () => void;
  readonly onError: (message: string) => void;
  readonly onInviteStatus: (message: string | null) => void;
  readonly onLeaveError: (message: string | null) => void;
  readonly onSelectedCircleId: (circleId: string | null) => void;
}

export function useCircleMutations({
  activeCircleId,
  onCloseCreate,
  onCloseInvite,
  onCloseRename,
  onError,
  onInviteStatus,
  onLeaveError,
  onSelectedCircleId,
}: UseCircleMutationsOptions) {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (name: string) => apiClient.createCircle({ name }),
    onSuccess: (circle) => {
      void queryClient.invalidateQueries({ queryKey: circlesQueryKey });
      onSelectedCircleId(circle.id);
      onCloseCreate();
    },
    onError: (error) => onError(error.message),
  });

  const renameMutation = useMutation({
    mutationFn: (name: string) => apiClient.updateCircle(activeCircleId!, { name }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: circlesQueryKey });
      onCloseRename();
    },
    onError: (error) => onError(error.message),
  });

  const leaveMutation = useMutation({
    mutationFn: () => apiClient.leaveCircle(activeCircleId!),
    onSuccess: () => {
      onSelectedCircleId(null);
      onLeaveError(null);
      void queryClient.invalidateQueries({ queryKey: circlesQueryKey });
    },
    onError: (error) => onLeaveError(error.message),
  });

  const inviteMutation = useMutation({
    mutationFn: (email: string) =>
      apiClient.inviteCircleMember(activeCircleId!, { email, role: "MEMBER" }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: circlesQueryKey });
      void queryClient.invalidateQueries({ queryKey: ["circle-members", activeCircleId] });
      void queryClient.invalidateQueries({ queryKey: ["circle-invites", activeCircleId] });
      onCloseInvite();
      onError("");
      onInviteStatus(
        result.outcome === "ADDED"
          ? `${result.member.displayName} was added to the circle.`
          : `Invite sent to ${result.invite.email}.`,
      );
    },
    onError: (error) => {
      onInviteStatus(null);
      onError(error.message);
    },
  });

  return { createMutation, inviteMutation, leaveMutation, renameMutation };
}
